import { HTTPS_URL_RE } from './formats'
import { CASE_TYPE_SCHEMA } from './schema'
import { collectMediaFieldNames } from './type-schema'

const PHONE_MEDIA_FIELD_NAMES = collectMediaFieldNames(CASE_TYPE_SCHEMA)
const GMAIL_ATTACHMENT_URL_PATH =
  /^device\.apps\.gmail\.gmail\[\d+\]\.attachments\[\d+\]\.url$/

export type MediaFieldPredicate = (path: string, field: string) => boolean

export function isAssetUrlField(path: string, field: string): boolean {
  return PHONE_MEDIA_FIELD_NAMES.has(field) || GMAIL_ATTACHMENT_URL_PATH.test(path)
}

export type MediaRef = {
  path: string
  field: string
  url: string
}

/**
 * Walk a case bundle and collect generated or uploaded asset URL fields.
 */
export function collectMediaUrls(
  value: unknown,
  basePath = '',
  isMediaField: MediaFieldPredicate = isAssetUrlField,
): MediaRef[] {
  const out: MediaRef[] = []

  if (Array.isArray(value)) {
    value.forEach((item, i) => {
      out.push(...collectMediaUrls(item, `${basePath}[${i}]`, isMediaField))
    })
    return out
  }

  if (typeof value !== 'object' || value === null) {
    return out
  }

  for (const [key, child] of Object.entries(value)) {
    const path = basePath ? `${basePath}.${key}` : key
    if (isMediaField(path, key)) {
      if (typeof child === 'string' && child.length > 0) {
        out.push({ path, field: key, url: child })
        continue
      }
      if (Array.isArray(child)) {
        child.forEach((item, i) => {
          if (typeof item === 'string' && item.length > 0) {
            out.push({ path: `${path}[${i}]`, field: key, url: item })
          }
        })
        continue
      }
    }
    out.push(...collectMediaUrls(child, path, isMediaField))
  }

  return out
}

export function isHttpsMediaUrl(url: string): boolean {
  return HTTPS_URL_RE.test(url)
}

export type MediaReadinessIssue = {
  path: string
  url: string
  reason: string
}

export type MediaReadinessResult = {
  ok: boolean
  total: number
  issues: MediaReadinessIssue[]
}

/**
 * Check media URLs against an allowlist host and an existence set of public URLs.
 * `existingUrls` should be the set of known-ready public URLs (from media_assets / R2).
 */
export function checkMediaReadiness(
  bundle: unknown,
  options: {
    allowedHostSuffixes: string[]
    existingUrls: Set<string>
    requireHttps?: boolean
    isMediaField?: MediaFieldPredicate
  },
): MediaReadinessResult {
  const requireHttps = options.requireHttps ?? true
  const refs = collectMediaUrls(bundle, '', options.isMediaField)
  const issues: MediaReadinessIssue[] = []

  for (const ref of refs) {
    if (requireHttps && !isHttpsMediaUrl(ref.url)) {
      issues.push({
        path: ref.path,
        url: ref.url,
        reason: 'media URL must be https',
      })
      continue
    }

    let host = ''
    try {
      host = new URL(ref.url).host
    } catch {
      issues.push({
        path: ref.path,
        url: ref.url,
        reason: 'invalid URL',
      })
      continue
    }

    const hostOk = options.allowedHostSuffixes.some(
      (suffix) => host === suffix || host.endsWith(`.${suffix}`),
    )
    if (options.allowedHostSuffixes.length > 0 && !hostOk) {
      issues.push({
        path: ref.path,
        url: ref.url,
        reason: `host "${host}" is not in the allowed media host list`,
      })
      continue
    }

    if (!options.existingUrls.has(ref.url)) {
      issues.push({
        path: ref.path,
        url: ref.url,
        reason: 'object not registered as ready on R2',
      })
    }
  }

  return {
    ok: issues.length === 0,
    total: refs.length,
    issues,
  }
}
